import React, { useEffect, useMemo, useState } from "react";
import { Box, Popover, TextField, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import { getMessageTemplates } from "../../../../../store/crm/message-templates";
import { getCustomerInsights } from "../../../../../store/crm/churn";
import {
  applyTemplateVars,
  buildTemplateContext,
  clinicNameOf,
  groupByCategory,
  unresolvedVars,
} from "../../../../../store/crm/message-templates/templateUtils";
import { ACCENT, ACCENT_LIGHT, PINK_BORDER } from "../../churnStyles";

/**
 * Template picker for the chat composer. Reads the clinic's saved templates
 * (Clinic Settings → Messaging → Message Templates) and inserts the chosen one
 * with its {{placeholders}} filled in from the customer being messaged.
 * Placeholders this thread can't fill are left visible so nobody sends a
 * half-finished sentence by accident.
 */
const TemplatePicker = ({ anchorEl, onClose, onPick, context = {} }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const { templates, loaded, loading } = useSelector(
    (state) => state.messageTemplatesStore
  );
  const user = useSelector((state) => state?.authStore?.user);
  const insights = useSelector((state) => state.churnStore?.insights || {});

  const open = !!anchorEl;
  const customerId = context?.user_id;
  const customerInsights = customerId ? insights[customerId] : null;

  // One fetch per session — templates change in settings, not mid-conversation.
  useEffect(() => {
    if (open && !loaded && !loading) {
      dispatch(getMessageTemplates({ active_only: 1 }));
    }
  }, [open, loaded, loading, dispatch]);

  // Both chat surfaces load insights when a thread opens; this only covers the
  // case where the picker is reached before that landed.
  useEffect(() => {
    if (open && customerId && !customerInsights) {
      dispatch(getCustomerInsights(customerId));
    }
  }, [open, customerId, customerInsights, dispatch]);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  // Placeholders resolve from live system data: the customer's name, the
  // clinic on the login, and their last booking / next expected date.
  const fullContext = useMemo(
    () =>
      buildTemplateContext({
        customerName: context?.name,
        clinicName: clinicNameOf(user),
        insights: customerInsights,
      }),
    [user, context?.name, customerInsights]
  );

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const active = (templates || []).filter((t) => t.is_active !== false);
    const matched = term
      ? active.filter(
          (t) =>
            `${t.name || ""}`.toLowerCase().includes(term) ||
            `${t.body || ""}`.toLowerCase().includes(term)
        )
      : active;
    return groupByCategory(matched);
  }, [templates, search]);

  const handlePick = (template) => {
    onPick(applyTemplateVars(template.body, fullContext));
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${PINK_BORDER}` } }}
    >
      <Box sx={{ width: 340, maxHeight: 420, display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${PINK_BORDER}` }}>
          <Typography
            sx={{
              fontSize: "13px",
              color: ACCENT,
              fontFamily: "helvetica-lt-std-bold",
              mb: 1,
            }}
          >
            Message templates
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "#9CA3AF", fontSize: 18, mr: 0.8 }} />,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "9px",
                fontSize: "13px",
                "& fieldset": { borderColor: PINK_BORDER },
                "&:hover fieldset": { borderColor: ACCENT },
                "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
              },
            }}
          />
        </Box>

        <Box sx={{ overflowY: "auto", p: 1 }}>
          {loading && !templates?.length && (
            <Typography sx={emptySx}>Loading templates…</Typography>
          )}
          {!loading && groups.length === 0 && (
            <Typography sx={emptySx}>
              {search
                ? "No template matches that search."
                : "No templates yet — add them in Clinic Settings → Messaging."}
            </Typography>
          )}

          {groups.map((group) => (
            <Box key={group.key} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "10px",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  fontFamily: "helvetica-lt-std-bold",
                  color: group.color,
                  px: 1,
                  py: "4px",
                }}
              >
                {group.label}
              </Typography>
              {group.templates.map((template) => {
                const missing = unresolvedVars(template.body, fullContext);
                return (
                  <Box
                    key={template.id}
                    onClick={() => handlePick(template)}
                    sx={{
                      p: 1,
                      borderRadius: "9px",
                      cursor: "pointer",
                      "&:hover": { background: ACCENT_LIGHT },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#111827",
                        fontFamily: "helvetica-lt-std-bold",
                      }}
                    >
                      {template.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#6B7280",
                        mt: "2px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {applyTemplateVars(template.body, fullContext)}
                    </Typography>
                    {missing.length > 0 && (
                      <Typography sx={{ fontSize: "11px", color: "#F59E0B", mt: "3px" }}>
                        Fill in {missing.join(", ")} before sending
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${PINK_BORDER}` }}>
          <Typography sx={{ fontSize: "11px", color: "#9CA3AF" }}>
            Manage these in Clinic Settings → Messaging → Message Templates.
          </Typography>
        </Box>
      </Box>
    </Popover>
  );
};

const emptySx = {
  fontSize: "12.5px",
  color: "#9CA3AF",
  textAlign: "center",
  py: 3,
  px: 2,
  lineHeight: 1.5,
};

export default TemplatePicker;
